/**
 * Money as people read it, in the active location's currency
 * (openspec/changes/location-bill-settings). Mirrors the API's
 * api/src/lib/utils/money.util.ts. Amounts are never converted, only labelled.
 *
 * The active currency is remembered with the other shop details: written when
 * a shop is chosen and refreshed from the API on every dashboard load.
 */
export const DEFAULT_CURRENCY = 'INR';
export const SUPPORTED_CURRENCIES = ['INR', 'AED', 'USD', 'LKR', 'BDT', 'SGD', 'MYR', 'GBP', 'EUR'] as const;

const CURRENCY_NAMES: Record<string, string> = {
	INR: 'Indian rupee',
	AED: 'UAE dirham',
	USD: 'US dollar',
	LKR: 'Sri Lankan rupee',
	BDT: 'Bangladeshi taka',
	SGD: 'Singapore dollar',
	MYR: 'Malaysian ringgit',
	GBP: 'British pound',
	EUR: 'Euro',
};

const isSupported = (code: unknown): code is string =>
	typeof code === 'string' && (SUPPORTED_CURRENCIES as readonly string[]).includes(code);

export function activeCurrency(): string {
	try {
		const stored = localStorage.getItem('currency');
		return isSupported(stored) ? stored : DEFAULT_CURRENCY;
	} catch {
		return DEFAULT_CURRENCY;
	}
}

export function rememberCurrency(code: string | null | undefined): void {
	try {
		localStorage.setItem('currency', isSupported(code) ? code : DEFAULT_CURRENCY);
	} catch {
		/* private mode: the default applies */
	}
}

const locale = (currency: string) => (currency === 'INR' ? 'en-IN' : 'en');

/** "₹1,25,000.00", "AED 1,250.00", "$1,250.00". `decimals` 0 drops the paise/cents. */
export function formatMoney(amount: number | string | null | undefined, currency: string = activeCurrency(), decimals = 2): string {
	const code = isSupported(currency) ? currency : DEFAULT_CURRENCY;
	return new Intl.NumberFormat(locale(code), {
		style: 'currency',
		currency: code,
		currencyDisplay: 'symbol',
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	}).format(Number(amount) || 0);
}

/** Just the sign: "₹", "AED", "$". For input adornments. */
export function currencySymbol(currency: string = activeCurrency()): string {
	const code = isSupported(currency) ? currency : DEFAULT_CURRENCY;
	const part = new Intl.NumberFormat(locale(code), { style: 'currency', currency: code, currencyDisplay: 'symbol' })
		.formatToParts(0)
		.find((p) => p.type === 'currency');
	return part?.value ?? code;
}

/** The sign as it goes in front of a number in text: "₹", "$", or "AED " with its space. */
export function currencyPrefix(currency: string = activeCurrency()): string {
	const symbol = currencySymbol(currency);
	return symbol.length > 1 ? `${symbol} ` : symbol;
}

/** "INR — Indian rupee (₹)", "AED — UAE dirham": for currency pickers. */
export function currencyLabel(code: string): string {
	const name = CURRENCY_NAMES[code] ?? code;
	const symbol = currencySymbol(code);
	return symbol === code ? `${code} — ${name}` : `${code} — ${name} (${symbol})`;
}

const trim = (n: number) => (Math.round(n * 10) / 10).toString().replace(/\.0$/, '');

/** Axis-scale amounts: ₹950 · ₹12.4K · ₹3.5L · ₹1.2Cr for INR; $950 · $12.4K · $3.5M elsewhere. */
export function formatMoneyCompact(value: number | null | undefined, currency: string = activeCurrency()): string {
	const code = isSupported(currency) ? currency : DEFAULT_CURRENCY;
	const prefix = currencyPrefix(code);
	const n = Number(value) || 0;
	const abs = Math.abs(n);
	const sign = (s: string) => `${prefix}${s}`;
	if (code === 'INR') {
		if (abs >= 1e7) return sign(`${trim(n / 1e7)}Cr`);
		if (abs >= 1e5) return sign(`${trim(n / 1e5)}L`);
	} else {
		if (abs >= 1e9) return sign(`${trim(n / 1e9)}B`);
		if (abs >= 1e6) return sign(`${trim(n / 1e6)}M`);
	}
	if (abs >= 1e3) return sign(`${trim(n / 1e3)}K`);
	return sign(`${Math.round(n)}`);
}
